import React from "react";
import styled, { keyframes } from "styled-components";
import * as Router from "next/router";
import axios from "axios";

import config from "../../config.client";
import * as u from "../../utils";
import * as gtag from "../../utils/gtag";
import Button from "../../components/Button";
import * as I from "../../components/Icons";
import * as L from "../../components/Layout";
import * as T from "../../components/Typography";
import * as UserContext from "../../components/UserContext";
import RxPaymentPlan from "@/components/ui/display/RxPaymentPlan";
import RxButton from "@/components/ui/inputs/RxButton";

export default function Select() {
  const router = Router.useRouter();
  const user = React.useContext(UserContext.Context);
  const [currency, setCurrency] = React.useState();
  const [prices, setPrices] = React.useState([]);
  React.useEffect(() => {
    getCurrency();
  }, [router.locale]);
  React.useEffect(() => {
    getPrices();
  }, [currency]);
  React.useEffect(() => {
    abandonBag();
  }, []);
  React.useEffect(() => {
    if (!user.id) router.push("/auth/login");
  }, []);
  const loading = !prices.length;

  return (
    <L.Container>
      <S.Layout>
        <S.TitleWrap>
          <T.H2>
            Select{" "}
            <S.RoxboroughFontWrap>
              <i>payment</i> plan
            </S.RoxboroughFontWrap>
          </T.H2>
          <T.CL>
            <b>Each plan comes with a complimentary 14 days on us.</b> After
            that you will be billed by the method of your choice. <br />
            Free trial wil not be applied with the use of any promo code or
            discount
          </T.CL>
        </S.TitleWrap>
        <S.Prices priceCount={prices.length}>
          {loading ? (
            <S.Loader>
              <Loader />
            </S.Loader>
          ) : (
            <>
              <S.PriceContainer>
                <RxPaymentPlan
                  colour={u.colours.teal}
                  id={prices[0].id}
                  titleColour={u.colours.shell}
                  title="Standard plan"
                  subTitle="Per month"
                  monthlyPrice="12/month"
                  name="price"
                  paymentInterval="billed monthly"
                  price={prices[0].unit_amount / 100}
                  onClick={() => goToPaymentPage(prices[0])}
                />
                <RxButton
                  buttonText="Choose this plan"
                  background={u.colours.teal}
                  textColour={u.colours.shell}
                  onClick={() => goToPaymentPage(prices[0])}
                />
              </S.PriceContainer>
              <S.PriceContainer>
                <RxPaymentPlan
                  colour={u.colours.freeBanana}
                  id={prices[1].id}
                  titleColour={u.colours.charcoal}
                  title="Best Value"
                  monthlyPrice="10 per month"
                  subTitle="Per year"
                  paymentInterval="billed yearly"
                  name="price"
                  price={prices[1].unit_amount / 100}
                  priceText="£120"
                  onClick={() => goToPaymentPage(prices[1])}
                />
                <RxButton
                  buttonText="Choose this plan"
                  background={u.colours.freeBanana}
                  textColour={u.colours.charcoal}
                  onClick={() => goToPaymentPage(prices[1])}
                />
              </S.PriceContainer>
            </>
          )}
        </S.Prices>
        <S.TitleWrap>
          <T.H2>
            <S.RoxboroughFontWrap>Just want to dip a toe?</S.RoxboroughFontWrap>
          </T.H2>
          <T.CL>
            The <S.Link onClick={goToFreeSite}>free version</S.Link> of
            AllBright Virtual allows you limited access to the platform, and you
            can upgrade any time. Selecting this option will not charge your
            card, but you will only have basic access to what’s on offer,
            excluding Mentor Matching, customisable profiles, and most events
            and courses.
          </T.CL>
        </S.TitleWrap>
      </S.Layout>
    </L.Container>
  );

  function goToPaymentPage(price) {
    gtag.event({
      name: gtag.GA_EVENT.DIGITAL_PAYMENT,
      category: "Click Payment Plan",
      label: price.recurring.interval,
      value: Math.ceil(price.unit_amount / 100),
    });

    router.push(`/digital/pay?priceId=${price.id}`);
  }

  function goToFreeSite() {
    gtag.event({
      name: gtag.GA_EVENT.DIGITAL_PAYMENT,
      category: "Click Payment Plan",
      label: "free",
      value: 0,
    });

    router.push(`/onboarding/welcome`);
  }

  /**
   *
   * This loader uses a stroke dash offset, set by CSS animation, to rotate the stroke into view.
   *
   */
  function Loader() {
    const svgProps = { width: 60, height: 60, transform: "rotate(-90 30 30)" };
    const circleProps = {
      r: 22,
      cx: 30,
      cy: 30,
      fill: "transparent",
      stroke: u.colours.teal,
      strokeWidth: 5,
      strokeDasharray: 138,
    };

    return (
      <svg {...svgProps}>
        <SL.Circle {...circleProps} />
      </svg>
    );
  }

  async function getCurrency() {
    const CURRENCY = { "en-gb": "gbp", "en-us": "usd", "en-au": "aud" };
    const maybeCurrency = CURRENCY[router.locale];
    if (maybeCurrency) return setCurrency(maybeCurrency);

    const URL = "https://extreme-ip-lookup.com/json"; // this might be ineffective
    const ipCountryData = await (await fetch(URL)).json();
    if (ipCountryData.countryCode === "GB") setCurrency("gbp");
    else if (ipCountryData.continent === "Europe") setCurrency("eur");
    else setCurrency("usd");
  }

  async function getPrices() {
    if (!currency) return;
    const url = "/api/stripe.get-prices-by-product-id";
    const body = { productId: config.DIGITAL_PRODUCT_ID, currency };
    const { data } = await axios.post(url, body);
    const nonZeroPrices = data.filter((p) => p.unit_amount);
    const sortedPrices = nonZeroPrices.sort(
      (a, b) => a.unit_amount - b.unit_amount
    );
    setPrices(sortedPrices);
  }

  async function abandonBag() {
    await axios.post("/api/user.abandon-bag", { stage: "INTERESTED" });
  }
}

const S = {};

S.Layout = styled.div`
  margin: 0 auto;
  padding: 0 5%;
  max-width: 1000px;
  display: flex;
  flex-direction: column;
  gap: 40px;
`;

S.TitleWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

S.RoxboroughFontWrap = styled.span`
  font-family:
    Roxborough CF,
    sans-serif;
  font-weight: 100;
`;

S.Prices = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 30px;

  @media ${u.device.mobile} {
    align-items: center;
  }

  @media ${u.device.tablet} {
    align-items: center;
    flex-direction: row;
    grid-gap: 20px;
  }

  @media ${u.device.desktop} {
    grid-gap: 40px;
  }
`;

S.PriceContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
  max-width: 400px;
`;

S.Loader = styled.div`
  margin: 0 auto;
  height: 189px;
`;

S.Button = styled(Button)`
  margin: 0 auto;
`;

S.ArrowRight = styled(I.ArrowRight)`
  height: 22px;
  margin-left: 10px;
`;

S.Link = styled.a`
  text-decoration: underline;
  cursor: pointer;
`;

const SL = {};

SL.Spin = keyframes`
  0% {
    stroke-dashoffset: 138;
  }
  100% {
    stroke-dashoffset: 0;
  }
`;

SL.Circle = styled.circle`
  animation: ${SL.Spin} 0.5s ease-out forwards;
`;
